import _ from 'lodash';
import { rgba } from 'polished';
import { ComponentPropsWithRef, PropsWithChildren } from 'react';
import { Box, Flex } from 'reflexbox';
import styled from 'styled-components';

import { useFeature } from '@treams/treams-hooks';
import { ReplyIcon } from '@treams/treams-icons';
import * as graphql from '@treams/treams-network';
import { IconButton, NoPrintWrapper, tokens } from '@treams/treams-ui';

const Bar = styled(Flex)`
	border-radius: 20px;
	background: ${(props) => props.theme.colors.white};
	box-shadow:
		0px 2px 4px ${rgba(tokens.text.secondary, 0.1)},
		0px 1px 2px ${rgba(tokens.text.secondary, 0.05)},
		0px 0px 0px 1px ${rgba(tokens.text.secondary, 0.25)} inset;
`;

const Container = styled(Box)`
	position: relative;
	padding-bottom: 20px;

	@media print {
		padding-bottom: 0;
	}
`;

const Holder = styled(Box)`
	position: absolute;
	bottom: 4px;
	right: -4px;
`;

const Emoji = styled(Flex)`
	align-items: center;
	justify-content: center;
	width: 24px;
	height: 24px;
	font-size: 20px;
`;

function EmojiButton(props: {
	targetID: string;
	targetType: graphql.CommentTargetType;
	metadata?: Record<string, string>;
	emoji: string;
	onRefetch?: () => void;
}) {
	const [createComment] = graphql.useCreateCommentMutation({
		variables: {
			input: {
				targetID: props.targetID,
				targetType: props.targetType,
				metadata: _.entries(props.metadata).map(([key, value]) => ({ key, value })),
				content: { ops: [{ insert: props.emoji }] },
			},
		},
	});

	const update = async () => {
		await createComment();
		if (props.onRefetch) {
			props.onRefetch();
		}
	};

	return <IconButton icon={<Emoji>{props.emoji}</Emoji>} type="plain" onClick={update} />;
}

type CommentBarProps = {
	targetID: string;
	targetType: graphql.CommentTargetType;
	metadata?: Record<string, string>;
	onComment: () => void;
	onRefetch?: () => void;
};

export default function CommentBar({
	targetID,
	targetType,
	metadata,
	onComment,
	onRefetch,
}: PropsWithChildren<CommentBarProps>) {
	const emojis = ['👍', '😃', '🙂', '🙏'];

	return (
		<Bar p={1}>
			{emojis.map((emoji, index) => (
				<EmojiButton
					key={index}
					targetID={targetID}
					targetType={targetType}
					metadata={metadata}
					emoji={emoji}
					onRefetch={onRefetch}
				/>
			))}
			<IconButton icon={<ReplyIcon />} type="plain" onClick={() => onComment()} />
		</Bar>
	);
}

function CommentBarContainer(
	props: PropsWithChildren<ComponentPropsWithRef<typeof CommentBar>> & {
		enabled?: boolean;
	},
) {
	const useFeedbackReply = useFeature(graphql.FeatureName.FeedbackReply);

	return (
		<Container>
			{props.children}
			{props.enabled !== false && useFeedbackReply && (
				<NoPrintWrapper>
					<Holder>
						<CommentBar {...props} />
					</Holder>
				</NoPrintWrapper>
			)}
		</Container>
	);
}

CommentBar.Container = CommentBarContainer;
